
import { useState } from 'react';
import { useForm, ControllerRenderProps } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { X, Plus, Loader2 } from 'lucide-react';
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage, 
  FormDescription 
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { 
  MesoCurriculumFormValues, 
  mesoCurriculumSchema, 
  beltColors 
} from './schema';
import { MesoCurriculum } from '@/services/roadmap/roadmapService';
import { useToast } from '@/hooks/use-toast';
import { RichTextEditor } from '../shared/RichTextEditor';

interface MesoCurriculumFormProps {
  mode?: 'create' | 'edit';
  initialData?: MesoCurriculum;
  onSubmit?: (data: MesoCurriculumFormValues) => Promise<void>;
  onCancel?: () => void;
  onSuccess?: () => void;
}

export function MesoCurriculumForm({ 
  mode = 'create',
  initialData, 
  onSubmit,
  onCancel,
  onSuccess
}: MesoCurriculumFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newTechnique, setNewTechnique] = useState('');
  const [newObjective, setNewObjective] = useState('');
  
  const defaultValues: Partial<MesoCurriculumFormValues> = {
    title: initialData?.title || '',
    belt_color: initialData?.belt_color || 'White',
    stripe_count: initialData?.stripe_count || 0,
    content: initialData?.content || '',
    image_url: initialData?.image_url || '',
    video_url: initialData?.video_url || '',
    key_techniques: initialData?.key_techniques || [],
    learning_objectives: initialData?.learning_objectives || [],
    order_index: initialData?.order_index || 0,
    is_active: initialData?.is_active ?? true,
  };

  const form = useForm<MesoCurriculumFormValues>({
    resolver: zodResolver(mesoCurriculumSchema),
    defaultValues,
  });

  const handleSubmit = async (data: MesoCurriculumFormValues) => {
    try {
      setIsSubmitting(true);
      
      if (onSubmit) {
        await onSubmit(data);
      }
      
      toast({
        title: mode === 'edit' ? 'Curriculum Updated' : 'Curriculum Created',
        description: `Successfully ${mode === 'edit' ? 'updated' : 'created'} curriculum entry.`,
      });
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: 'Error',
        description: 'Failed to save curriculum. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addTechnique = () => {
    if (newTechnique.trim()) {
      const currentTechniques = form.getValues('key_techniques') || [];
      form.setValue('key_techniques', [...currentTechniques, newTechnique.trim()]);
      setNewTechnique('');
    }
  };

  const removeTechnique = (index: number) => {
    const currentTechniques = form.getValues('key_techniques') || [];
    form.setValue('key_techniques', currentTechniques.filter((_: string, i: number) => i !== index));
  };

  const addObjective = () => {
    if (newObjective.trim()) {
      const currentObjectives = form.getValues('learning_objectives') || [];
      form.setValue('learning_objectives', [...currentObjectives, newObjective.trim()]);
      setNewObjective('');
    }
  };

  const removeObjective = (index: number) => {
    const currentObjectives = form.getValues('learning_objectives') || [];
    form.setValue('learning_objectives', currentObjectives.filter((_: string, i: number) => i !== index));
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Title */}
          <FormField
            control={form.control}
            name="title"
            render={({ field }: { field: ControllerRenderProps<MesoCurriculumFormValues, 'title'> }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Enter curriculum title" {...field} />
                </FormControl>
                <FormDescription>
                  The title for this curriculum entry
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {/* Order Index */}
          <FormField
            control={form.control}
            name="order_index"
            render={({ field }: { field: ControllerRenderProps<MesoCurriculumFormValues, 'order_index'> }) => (
              <FormItem>
                <FormLabel>Display Order</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    {...field}
                    value={field.value?.toString() || ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                </FormControl>
                <FormDescription>
                  Controls the display order (lower numbers appear first)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Belt Color */}
          <FormField
            control={form.control}
            name="belt_color"
            render={({ field }: { field: ControllerRenderProps<MesoCurriculumFormValues, 'belt_color'> }) => (
              <FormItem>
                <FormLabel>Belt Color</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a belt color" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {beltColors.map((belt) => (
                      <SelectItem key={belt.value} value={belt.value}>
                        {belt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>
                  The corresponding belt rank
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Stripe Count */}
          <FormField
            control={form.control}
            name="stripe_count"
            render={({ field }: { field: ControllerRenderProps<MesoCurriculumFormValues, 'stripe_count'> }) => (
              <FormItem>
                <FormLabel>Stripe Count</FormLabel>
                <Select 
                  onValueChange={(value) => field.onChange(Number(value))} 
                  defaultValue={String(field.value)}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select stripe count" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {[0, 1, 2, 3, 4].map((count) => (
                      <SelectItem key={count} value={String(count)}>
                        {count}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>
                  Number of stripes (0-4)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Image URL */}
          <FormField
            control={form.control}
            name="image_url"
            render={({ field }: { field: ControllerRenderProps<MesoCurriculumFormValues, 'image_url'> }) => (
              <FormItem>
                <FormLabel>Image URL</FormLabel>
                <FormControl>
                  <Input placeholder="https://example.com/image.jpg" {...field} />
                </FormControl>
                <FormDescription>
                  Optional image URL
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Video URL */}
          <FormField
            control={form.control}
            name="video_url"
            render={({ field }: { field: ControllerRenderProps<MesoCurriculumFormValues, 'video_url'> }) => (
              <FormItem>
                <FormLabel>Video URL</FormLabel>
                <FormControl>
                  <Input placeholder="https://youtube.com/watch?v=..." {...field} />
                </FormControl>
                <FormDescription>
                  Optional video URL (YouTube)
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Is Active */}
          <FormField
            control={form.control}
            name="is_active"
            render={({ field }: { field: ControllerRenderProps<MesoCurriculumFormValues, 'is_active'> }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Active Status</FormLabel>
                  <FormDescription>
                    Whether this curriculum entry should be visible
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        
        {/* Content */}
        <RichTextEditor
          form={form}
          name="content"
          label="Content"
          description="Curriculum details in markdown format. Add comprehensive descriptions, examples, and explanations."
          placeholder="# Main Section Title
## Subsection
Key concepts to understand:
- Point 1
- Point 2

## Another Subsection
More detailed explanation..."
        />

        {/* Key Techniques */}
        <FormField
          control={form.control}
          name="key_techniques"
          render={({ field }: { field: ControllerRenderProps<MesoCurriculumFormValues, 'key_techniques'> }) => (
            <FormItem>
              <FormLabel>Key Techniques</FormLabel>
              <div className="flex gap-2">
                <Input
                  value={newTechnique}
                  onChange={(e) => setNewTechnique(e.target.value)}
                  placeholder="Add technique..."
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTechnique();
                    }
                  }}
                />
                <Button type="button" onClick={addTechnique} size="sm">
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {field.value?.map((technique: string, index: number) => (
                  <Badge 
                    key={`${technique}-${index}`} 
                    variant="secondary"
                    className="flex items-center gap-1 px-2 py-1"
                  >
                    {technique}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0"
                      onClick={() => removeTechnique(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
              <FormDescription>
                Key techniques covered in this curriculum section
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Learning Objectives */}
        <FormField
          control={form.control}
          name="learning_objectives"
          render={({ field }: { field: ControllerRenderProps<MesoCurriculumFormValues, 'learning_objectives'> }) => (
            <FormItem>
              <FormLabel>Learning Objectives</FormLabel>
              <div className="flex gap-2">
                <Input
                  value={newObjective}
                  onChange={(e) => setNewObjective(e.target.value)}
                  placeholder="Add learning objective..."
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addObjective();
                    }
                  }}
                />
                <Button type="button" onClick={addObjective} size="sm">
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {field.value?.map((objective: string, index: number) => (
                  <Badge 
                    key={`${objective}-${index}`} 
                    variant="secondary"
                    className="flex items-center gap-1 px-2 py-1"
                  >
                    {objective}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0"
                      onClick={() => removeObjective(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
              <FormDescription>
                Learning objectives for this curriculum section
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {initialData ? 'Update' : 'Create'} Curriculum
          </Button>
        </div>
      </form>
    </Form>
  );
}
